import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, AlertCircle, Save, Maximize2, Minimize2 } from 'lucide-react';
import { schemasApi, appsApi, type Schema } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DataModeler, useDataModelerStore } from '../features/data-modeler';

export function DataModelerPage() {
  const { schemaId } = useParams<{ schemaId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'modeler' | 'settings'>('modeler');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Get store actions
  const { importModel, exportModel, reset } = useDataModelerStore();

  // Fetch schema details
  const {
    data: schema,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['schema', schemaId],
    queryFn: () => schemasApi.get(schemaId!),
    enabled: !!schemaId,
  });

  // Fetch apps for reference
  const { data: appsData } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  });

  // Update form data and load schema into store when loaded
  useEffect(() => {
    if (schema) {
      setFormData({
        name: schema.name || '',
        description: schema.description || '',
      });

      // Load schema data into the data modeler store
      try {
        const fieldsData = schema.fields ? JSON.parse(schema.fields) : { entities: [], relations: [] };
        importModel({
          id: schema.id,
          name: schema.name,
          description: schema.description,
          entities: fieldsData.entities || [],
          relations: fieldsData.relations || [],
          createdAt: schema.created_at,
          updatedAt: schema.updated_at,
        });
      } catch (e) {
        console.error('Failed to parse schema fields:', e);
        // Initialize with empty model
        importModel({
          id: schema.id,
          name: schema.name,
          description: schema.description,
          entities: [],
          relations: [],
          createdAt: schema.created_at,
          updatedAt: schema.updated_at,
        });
      }
    }

    // Cleanup on unmount
    return () => {
      reset();
    };
  }, [schema, importModel, reset]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<Schema>) => schemasApi.update(schemaId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schema', schemaId] });
      queryClient.invalidateQueries({ queryKey: ['schemas'] });
      setHasChanges(false);
    },
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Export current model state from the data modeler
    const modelData = exportModel();

    updateMutation.mutate({
      name: formData.name,
      description: formData.description,
      fields: JSON.stringify({
        entities: modelData.entities,
        relations: modelData.relations,
      }),
    });
  };

  const apps = appsData?.items || [];
  const appName = apps.find(a => a.id === schema?.app_id)?.name || 'N/A';

  const getEntityCount = (fields: string) => {
    try {
      const parsed = JSON.parse(fields);
      if (parsed.entities && Array.isArray(parsed.entities)) {
        return parsed.entities.length;
      }
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        <span className="ml-2 text-secondary-500">Chargement du modèle...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/data')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>Erreur lors du chargement: {(error as Error).message}</span>
          </div>
        </div>
      </div>
    );
  }

  // Fullscreen mode render
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-secondary-950">
        {/* Fullscreen header */}
        <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between pointer-events-none">
          <div className="flex items-center gap-3 pointer-events-auto bg-white/90 dark:bg-secondary-900/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg border border-secondary-200 dark:border-secondary-700">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
              {schema?.name || 'Modèle de données'}
            </h2>
            <span className="text-sm text-secondary-500">
              {appName} • {schema?.fields ? getEntityCount(schema.fields) : 0} entités
            </span>
          </div>
          <div className="flex items-center gap-2 pointer-events-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
              className="bg-white/90 dark:bg-secondary-900/90 backdrop-blur-sm"
            >
              {updateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Enregistrer
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullscreen(false)}
              className="bg-white/90 dark:bg-secondary-900/90 backdrop-blur-sm"
            >
              <Minimize2 className="h-4 w-4 mr-2" />
              Réduire
            </Button>
          </div>
        </div>
        {/* Modeler in fullscreen */}
        <div className="h-full">
          <DataModeler />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-200 dark:border-secondary-800 bg-white dark:bg-secondary-900">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/data')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
              {schema?.name || 'Modèle de données'}
            </h2>
            <p className="text-sm text-secondary-500">
              {appName} • {schema?.fields ? getEntityCount(schema.fields) : 0} entités
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Tabs */}
          <div className="flex rounded-lg border border-secondary-200 dark:border-secondary-700 mr-4">
            <button
              className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                activeTab === 'modeler'
                  ? 'bg-primary-500 text-white'
                  : 'text-secondary-600 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800'
              }`}
              onClick={() => setActiveTab('modeler')}
            >
              Modélisation
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                activeTab === 'settings'
                  ? 'bg-primary-500 text-white'
                  : 'text-secondary-600 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800'
              }`}
              onClick={() => setActiveTab('settings')}
            >
              Paramètres
            </button>
          </div>

          {activeTab === 'modeler' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullscreen(true)}
              title="Plein écran"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}

          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'modeler' ? (
        <div className="flex-1">
          <DataModeler />
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-2xl space-y-6">
            <div className="rounded-xl border border-secondary-200 bg-white p-6 dark:border-secondary-800 dark:bg-secondary-900">
              <h3 className="text-md font-medium text-secondary-900 dark:text-white mb-4">
                Informations générales
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du modèle *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Mon Modèle"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Description du modèle..."
                    rows={4}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-secondary-200 bg-white p-6 dark:border-secondary-800 dark:bg-secondary-900">
              <h3 className="text-md font-medium text-secondary-900 dark:text-white mb-4">
                Informations
              </h3>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-secondary-500">ID</dt>
                  <dd className="font-mono text-secondary-900 dark:text-white text-xs mt-1">
                    {schema?.id}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Slug</dt>
                  <dd className="text-secondary-900 dark:text-white mt-1">
                    {schema?.slug}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Application</dt>
                  <dd className="text-secondary-900 dark:text-white mt-1">
                    {appName}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Nombre d'entités</dt>
                  <dd className="text-secondary-900 dark:text-white mt-1">
                    {schema?.fields ? getEntityCount(schema.fields) : 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-secondary-500">Créé le</dt>
                  <dd className="text-secondary-900 dark:text-white mt-1">
                    {schema?.created_at && new Date(schema.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </dd>
                </div>
              </dl>
            </div>

            {updateMutation.isError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-400">
                  Erreur: {(updateMutation.error as Error).message}
                </p>
              </div>
            )}

            {updateMutation.isSuccess && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                <p className="text-sm text-green-700 dark:text-green-400">
                  Modèle mis à jour avec succès !
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataModelerPage;
